import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  CheckIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import ColumnSearch from "../../../components/ColumnSearch";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../../../stores/useDialogStore";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import { toast } from "react-toastify";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";

export default function UsersList() {
  const [status, setStatus] = useState("Approved");
  const userinfo = getUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialogStore();

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const editCellMutation = useMutation({
    mutationFn: ({ userId, field, value }) =>
      axiosInstance.patch(`${window.REACT_APP_API_ENDPOINT}/users/${userId}`, {
        [field]: value,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast.success("User updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update user. Please try again.");
      console.error("Update user error:", error);
    },
  });

  const roleOptions = [
    { id: "Admin", name: "Admin" },
    { id: "Technician", name: "Technician" },
    { id: "Stock Manager", name: "Stock Manager" },
  ];

  const { data: users, isLoading } = useQuery({
    queryKey: ["users", status, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      params.set("status", status);

      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/users?${params.toString()}`
      );
      return response.data;
    },
    keepPreviousData: true,
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    params.set("status", status);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    params.set("status", status);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleClearFilters = () => {
    navigate(`${location.pathname}?status=${status}`);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/users/${id}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast.success("User deleted successfully");
      closeDialog();
    },
    onError: (error) => {
      console.error("Delete user error:", error);
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      await axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/users/${id}/status?status=${status}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      console.error("Status update error:", error);
    },
  });

  const handleDelete = (id, userName) => {
    openDialog({
      title: "Delete User",
      content: (
        <div>
          <p>Are you sure you want to delete user "{userName}"?</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={() => closeDialog()} variant="secondary" size="sm">
              Cancel
            </Button>
            <Button
              onClick={() => {
                deleteMutation.mutate(id);
              }}
              variant="danger"
              icon={<TrashIcon className="h-4 w-4" />}
              size="sm"
              className="p-1"
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleStatusChange = (id, newStatus) => {
    statusMutation.mutate({ id, status: newStatus });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {userinfo.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Users List
                </h4>

                <div className="flex flex-col lg:flex-row gap-2 items-center">
                  <Button
                    onClick={handleClearFilters}
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                  >
                    Clear Filters
                  </Button>
                  <Button
                    href="/admin/users/pending"
                    variant="outline-success"
                    size="sm"
                    icon={<CheckIcon className="h-4 w-4" />}
                  >
                    Approve User
                  </Button>
                  <Button
                    href="/admin/users/form"
                    variant="outline-info"
                    size="sm"
                    icon={<PlusIcon className="h-4 w-4" />}
                  >
                    Register User
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="First Name"
                            columnKey="name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by First Name..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Last Name"
                            columnKey="last_name"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Last Name..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="User ID"
                            columnKey="user_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by User ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Username"
                            columnKey="username"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Username..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Status"
                            columnKey="status"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Status..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Role"
                            columnKey="role"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Role..."
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : users?.data?.data?.length == 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <NoData message="No users found" />
                        </td>
                      </tr>
                    ) : (
                      users?.data?.data?.map((user, index) => (
                        <tr
                          key={user.id}
                          className={`divide-y divide-gray-200 ${
                            index % 2 === 0 ? "bg-white" : "bg-gray-100"
                          }`}
                        >
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={user.name}
                                displayValue={user.name}
                                id={user.id}
                                field="name"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    userId: data.id,
                                    field: "name",
                                    value: data.value.split(" ")[0],
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <EditableCell
                                value={user.last_name}
                                displayValue={user.last_name}
                                id={user.id}
                                field="last_name"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    userId: data.id,
                                    field: "last_name",
                                    value: data.value.split(" ")[0],
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={user.user_id}
                              displayValue={user.user_id}
                              id={user.id}
                              field="user_id"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  userId: data.id,
                                  field: "user_id",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <EditableCell
                              value={user.username}
                              displayValue={user.username}
                              id={user.id}
                              maxLength={20}
                              field="username"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  userId: data.id,
                                  field: "username",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              {user.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            {user.role == "SuperAdmin" ? (
                              <span className="text-gray-500">{user.role}</span>
                            ) : (
                              <EditableSelectCell
                                value={user.role}
                                displayValue={user.role}
                                id={user.id}
                                options={roleOptions}
                                field="role"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    userId: data.id,
                                    field: "role",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          {status === "Approved" && (
                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                              <div className="flex items-center pl-4 gap-2">
                                {user.role !== "SuperAdmin" && (
                                  <Button
                                    href={"/admin/users/" + user.id + "/edit"}
                                    variant="outline-info"
                                    icon={
                                      <PencilSquareIcon className="h-4 w-4 " />
                                    }
                                    size="icon"
                                    className="p-1"
                                  />
                                )}
                                {userinfo.role === "SuperAdmin" &&
                                  user.role !== "SuperAdmin" && (
                                    <Button
                                      onClick={() =>
                                        handleDelete(
                                          user.id,
                                          `${user.name} ${user.last_name}`
                                        )
                                      }
                                      variant="outline-danger"
                                      icon={<TrashIcon className="h-4 w-4 " />}
                                      size="icon"
                                      className="p-1"
                                    />
                                  )}
                              </div>
                            </td>
                          )}

                          {status === "Pending" && (
                            <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                              <div className="flex items-center pl-4">
                                <Button
                                  onClick={() =>
                                    handleStatusChange(user.id, "Approved")
                                  }
                                  icon={
                                    <CheckIcon
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  }
                                  size="icon"
                                  className="p-1"
                                />
                                <Button
                                  onClick={() =>
                                    handleStatusChange(user.id, "Rejected")
                                  }
                                  icon={
                                    <XMarkIcon
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  }
                                  size="icon"
                                  className="p-1"
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={users?.data?.current_page}
                last_page={users?.data?.last_page}
                next_page_url={users?.data?.next_page_url}
                prev_page_url={users?.data?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
